import React, {useContext, useEffect} from "react";
import {createUseStyles} from "react-jss";
import {useImmer} from "use-immer";
import {EngageAppContext} from "../../App";
import {TriviaStatus, TriviaStatusTime} from "../../state/state";
import {TriviaAction} from "../../messages/messages";
import TextField from "engage-client/components/TextField";
import {isUndefined} from "engage-common/utils";
import produce from "immer";

const useStyles = createUseStyles({
    optionContainer: {
        display:"flex",
        flexDirection:"column",
        "& > div": {
            margin: [8,0]
        }
    },
    buttonContainer: {
        display: "flex",
        flexDirection: "row",
    },
    button: {
        margin: 8
    },
});

function durationInSeconds(duration?:number):string {
    if(!isUndefined(duration)) {
        return (duration / 1000).toString();
    }
    return "";
}

function durationsInStrings(durations:{readonly [key in TriviaStatus]? : number}) : {readonly [key in TriviaStatus]? : string} {
    return {
        [TriviaStatus.Starting]: durationInSeconds(durations[TriviaStatus.Starting] ?? TriviaStatusTime.starting),
        [TriviaStatus.QuestionStart]: durationInSeconds(durations[TriviaStatus.QuestionStart] ?? TriviaStatusTime.question_start),
        [TriviaStatus.QuestionSubmit]: durationInSeconds(durations[TriviaStatus.QuestionSubmit]),
        [TriviaStatus.QuestionSummary]: durationInSeconds(durations[TriviaStatus.QuestionSummary] ?? TriviaStatusTime.question_summary),
        [TriviaStatus.QuestionAnswer]: durationInSeconds(durations[TriviaStatus.QuestionAnswer] ?? TriviaStatusTime.question_answer),
        [TriviaStatus.QuestionDetails]: durationInSeconds(durations[TriviaStatus.QuestionDetails] ?? TriviaStatusTime.question_details),
    };
}

export default function QuestionTiming() {
    const {sendMessage, appState} = useContext(EngageAppContext.current);
    const classes = useStyles();
    const [durations, setDurations] = useImmer(durationsInStrings(appState.common.gameOptions.durationOverrides));
    useEffect(() => {
        setDurations(draft => {
            Object.assign(draft, durationsInStrings(appState.common.gameOptions.durationOverrides));
        });
    }, [appState.common.gameOptions, setDurations]);

    const handleDurationChange = (name: TriviaStatus) => (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        let value = event.target.value;
        setDurations(draft => {
            draft[name] = value;
            let valueAsNumber = Number(value);
            if(valueAsNumber > 0) {
                produce(appState.common.gameOptions, draft=>{
                    draft.durationOverrides[name] = valueAsNumber * 1000;
                    sendMessage({type: TriviaAction.Options, gameOptions: draft});
                });
            }
        });
    };

    const handleDurationBlur = (name: TriviaStatus) => (
        event: React.FocusEvent<HTMLInputElement>
    ) => {
        let value = event.target.value;
        let valueAsNumber = Number(value);
        if(appState.common.gameOptions.durationOverrides[name] === valueAsNumber * 1000) {
            //the latest value is already stored
            return;
        }
        if(valueAsNumber <= 0) {
            if(name === TriviaStatus.QuestionSubmit) {
                value = "";
                valueAsNumber = 0;
            } else {
                //Don't let the value be 0 or below
                valueAsNumber = TriviaStatusTime[name] / 1000;
                value = valueAsNumber.toString();
            }
        }
        setDurations(draft => {
            draft[name] = value;
            produce(appState.common.gameOptions, optionsDraft=> {
                if(valueAsNumber*1000 === TriviaStatusTime[name]) {
                    //Reset to default
                    delete optionsDraft.durationOverrides[name];
                } else {
                    //Store in milliseconds
                    optionsDraft.durationOverrides[name] = valueAsNumber * 1000;
                }
                sendMessage({type:TriviaAction.Options, gameOptions:optionsDraft});
            });
        });
    };

    return (
        <>
            <div className={classes.optionContainer}>
                <TextField labelText="Starting"
                           name={TriviaStatus.Starting}
                           type="number"
                           placeholder={`${TriviaStatusTime.starting/1000}`}
                           value={durations[TriviaStatus.Starting]}
                           onChange={handleDurationChange(TriviaStatus.Starting)}
                           onBlur={handleDurationBlur(TriviaStatus.Starting)}
                           helperText="Countdown to the game"
                           variant="filled" backgroundColor="#D2DAE0" backgroundHoverColor="#B4C1CB"
                />
                <TextField labelText="Q. Start"
                           name={TriviaStatus.QuestionStart}
                           type="number"
                           placeholder={`${TriviaStatusTime.question_start/1000}`}
                           value={durations[TriviaStatus.QuestionStart]}
                           onChange={handleDurationChange(TriviaStatus.QuestionStart)}
                           onBlur={handleDurationBlur(TriviaStatus.QuestionStart)}
                           helperText="Shows the question prompt"
                           variant="filled" backgroundColor="#D2DAE0" backgroundHoverColor="#B4C1CB"
                />
                <TextField labelText="Q. Submit"
                           name={TriviaStatus.QuestionSubmit}
                           type="number"
                           placeholder="Varies per type"
                           value={durations[TriviaStatus.QuestionSubmit]}
                           onChange={handleDurationChange(TriviaStatus.QuestionSubmit)}
                           onBlur={handleDurationBlur(TriviaStatus.QuestionSubmit)}
                           helperText="Players time to submit their answers"
                           variant="filled" backgroundColor="#D2DAE0" backgroundHoverColor="#B4C1CB"
                />
                <TextField labelText="Q. Summary"
                           name={TriviaStatus.QuestionSummary}
                           type="number"
                           placeholder={`${TriviaStatusTime.question_summary/1000}`}
                           value={durations[TriviaStatus.QuestionSummary]}
                           onChange={handleDurationChange(TriviaStatus.QuestionSummary)}
                           onBlur={handleDurationBlur(TriviaStatus.QuestionSummary)}
                           helperText="Shows the answer distribution"
                           variant="filled" backgroundColor="#D2DAE0" backgroundHoverColor="#B4C1CB"
                />
                <TextField labelText="Q. Answer"
                           name={TriviaStatus.QuestionAnswer}
                           type="number"
                           placeholder={`${TriviaStatusTime.question_answer/1000}`}
                           value={durations[TriviaStatus.QuestionAnswer]}
                           onChange={handleDurationChange(TriviaStatus.QuestionAnswer)}
                           onBlur={handleDurationBlur(TriviaStatus.QuestionAnswer)}
                           helperText="Shows the right answer and lets the user know if they got it right"
                           variant="filled" backgroundColor="#D2DAE0" backgroundHoverColor="#B4C1CB"
                />
                <TextField labelText="Q. Details"
                           name={TriviaStatus.QuestionDetails}
                           type="number"
                           placeholder={`${TriviaStatusTime.question_details/1000}`}
                           value={durations[TriviaStatus.QuestionDetails]}
                           onChange={handleDurationChange(TriviaStatus.QuestionDetails)}
                           onBlur={handleDurationBlur(TriviaStatus.QuestionDetails)}
                           helperText="Shows the explanation for the answer and gives host time to banter"
                           variant="filled" backgroundColor="#D2DAE0" backgroundHoverColor="#B4C1CB"
                />
            </div>
        </>
    );
};